/**
 * /admin/users
 *
 * User management page. Lists all users with role, status, last login.
 * Inline actions: change role (dropdown), activate/deactivate toggle.
 * Self-protection: can't change your own role or deactivate yourself.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../../api/client.js';
import { listUsers, updateUserRole, updateUserActive } from '../../api/users.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { usePageTitle } from '../../hooks/usePageTitle.js';
import './UsersAdminPage.css';


function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date();
  const diffMs = today - d;
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffDays < 1) return 'today';
  if (diffDays < 2) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}


export default function UsersAdminPage() {
  usePageTitle('Admin · Users');

  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await listUsers();
      setUsers(data);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.detail : 'failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const activeCount = users.filter((u) => u.is_active).length;

  return (
    <>
      <div className="admin-page-head">
        <div className="admin-crumbs">
          <a href="/admin">admin</a>
          <span className="sep">/</span>
          users
        </div>
        <div className="admin-section-head">
          <div>
            <h1 className="admin-section-title">Users</h1>
            <p className="admin-section-sub">
              everyone who's made it past the velvet rope
            </p>
          </div>
        </div>
      </div>

      <div className="admin-content">
        {loading ? (
          <div className="users-empty">loading…</div>
        ) : loadError ? (
          <div className="users-empty error">⚠ {loadError}</div>
        ) : users.length === 0 ? (
          <div className="users-empty">
            no users yet. that's… concerning.
          </div>
        ) : (
          <>
            <div className="users-summary">
              {activeCount} active · {users.length} total
            </div>

            <div className="users-table">
              <div className="users-row users-row-head">
                <div>User</div>
                <div>Role</div>
                <div>Status</div>
                <div>Joined</div>
                <div>Last login</div>
                <div style={{ textAlign: 'right' }}>Actions</div>
              </div>

              {users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isSelf={currentUser?.id === u.id}
                  onUpdate={loadUsers}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}


function UserRow({ user, isSelf, onUpdate }) {
  const [changingRole, setChangingRole] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState('');

  async function handleRoleChange(newRole) {
    if (newRole === user.role) return;
    setChangingRole(true);
    setError('');
    try {
      await updateUserRole(user.id, newRole);
      onUpdate();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'failed to update role');
    } finally {
      setChangingRole(false);
    }
  }

  async function handleToggleActive() {
    const action = user.is_active ? 'deactivate' : 'reactivate';
    if (!confirm(`${action} ${user.display_name} (${user.username})?`)) return;

    setToggling(true);
    setError('');
    try {
      await updateUserActive(user.id, !user.is_active);
      onUpdate();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : `failed to ${action}`);
    } finally {
      setToggling(false);
    }
  }

  return (
    <div className={`users-row ${!user.is_active ? 'inactive' : ''}`}>
      <div className="users-identity">
        <div className="users-avatar">
          {user.avatar_url
            ? <img src={`${user.avatar_url}?v=${Date.now()}`} alt="" style={{width:'100%',height:'100%',borderRadius:'50%',objectFit:'cover'}} />
            : user.display_name.slice(0, 2).toUpperCase()
          }
        </div>
        <div>
          <div className="users-display-name">
            {user.display_name}
            {isSelf && <span className="users-you">you</span>}
          </div>
          <div className="users-meta">
            {user.username} · {user.email}
          </div>
        </div>
      </div>

      <div className="users-role-cell">
        {isSelf ? (
          <span className={`users-role-badge ${user.role}`}>{user.role}</span>
        ) : (
          <select
            className="users-role-select"
            value={user.role}
            onChange={(e) => handleRoleChange(e.target.value)}
            disabled={changingRole}
          >
            <option value="admin">admin</option>
            <option value="standard">standard</option>
            <option value="guest">guest</option>
          </select>
        )}
      </div>

      <div>
        <span className={`users-status ${user.is_active ? 'active' : 'inactive'}`}>
          {user.is_active ? 'active' : 'inactive'}
        </span>
      </div>

      <div className="users-date">{formatDate(user.created_at)}</div>
      <div className="users-date">{formatDate(user.last_login_at)}</div>

      <div className="users-actions">
        {error && <span className="users-row-error" title={error}>⚠</span>}
        {!isSelf && (
          <button
            className={`admin-btn ${user.is_active ? 'admin-btn-danger-ghost' : 'admin-btn-ghost'}`}
            onClick={handleToggleActive}
            disabled={toggling}
          >
            {toggling
              ? '…'
              : user.is_active
                ? 'deactivate'
                : 'reactivate'}
          </button>
        )}
      </div>
    </div>
  );
}
